<template>
  <div class="position-relative">
    <div class="cg-strip" @mouseleave="hoverIndex = null">
      <div
        v-for="(cell, index) in cells"
        :key="index"
        class="cg-strip-cell"
        :class="{ 'cg-strip-cell--marked': cell.mark }"
        :style="cellStyle(cell)"
        role="img"
        tabindex="0"
        :aria-label="`${cell.period}: ${cell.statusLabel}`"
        @mouseenter="onEnter(index, $event)"
        @focus="onEnter(index, $event)"
        @blur="hoverIndex = null"
      />
    </div>
    <div v-if="labels" class="cg-strip text-body-secondary cg-labels">
      <span
        v-for="(cell, index) in cells"
        :key="index"
        class="cg-strip-cell text-center"
        >{{ cell.label }}</span
      >
    </div>
    <HoverPopover v-if="hovered" :cell="hovered" :left="popoverLeft" :flip />
  </div>
</template>

<script setup lang="ts">
import type { StripCell } from "@/compliance_groups/fixture/derive";
import { FILL } from "@/compliance_groups/fixture/fills";
import { computed, ref } from "vue";
import HoverPopover from "./HoverPopover.vue";

const props = withDefaults(
  defineProps<{ cells: StripCell[]; height?: number; labels?: boolean }>(),
  {
    height: 15,
    labels: false,
  }
);

const hoverIndex = ref<number | null>(null);
const flip = ref(false);

const hovered = computed(() =>
  hoverIndex.value === null ? null : props.cells[hoverIndex.value]
);

// A partly delivered month is hatched: diagonal stripes of the received colour and the cell's own
// state colour. Deliberately not proportional (the cells are too small to read a share) and
// distinct from the under-forecast marker, which is a flat rule along the bottom edge.
const cellStyle = (cell: StripCell) => ({
  height: `${props.height}px`,
  background: cell.partial
    ? `repeating-linear-gradient(135deg, ${FILL.inAccount} 0 4px, ${FILL[cell.fill]} 4px 8px)`
    : FILL[cell.fill],
});

// Centre the popover on the cell but never let it leave the strip.
const popoverLeft = computed(() => {
  if (hoverIndex.value === null) return "0px";
  const n = props.cells.length || 1;
  return `clamp(0px, calc(${(((hoverIndex.value + 0.5) / n) * 100).toFixed(2)}% - 116px), calc(100% - 232px))`;
});

const onEnter = (index: number, event: Event) => {
  hoverIndex.value = index;
  // Open downward when there is not enough room above inside the viewport.
  const top =
    (event.currentTarget as HTMLElement | null)?.getBoundingClientRect().top ??
    0;
  flip.value = top < 230;
};
</script>

<style scoped lang="scss">
.cg-labels {
  font-size: 11px;
  letter-spacing: 0.02em;
  margin-top: 4px;
}
</style>
